import React, { useState, useEffect } from "react";
import axios from "axios";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ReportePDF from "./ReportePDF";
import ReporteClientePDF from "./ReporteClientePDF";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";
const LOGO = process.env.PUBLIC_URL + "/image.png";

const fmtFecha = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const fmtMonto = (m, moneda) => {
  if (!m) return "—";
  const n = parseFloat(m).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return (moneda === "USD" ? "USD " : "$") + n;
};
const hoyISO = () => new Date().toISOString().slice(0, 10);

// ─── Config de reportes (agregar uno = sumar un objeto acá) ───────────────────
const REPORTES = [
  {
    key: "entregas_vencidas",
    tipo: "entregas_vencidas",
    titulo: "Entregas vencidas",
    descripcion: "Órdenes de compra con fecha de entrega pasada que todavía no figuran como entregadas.",
    columnas: [
      { label: "N° OC", flex: 0.8 },
      { label: "Cliente", flex: 2 },
      { label: "Proveedor", flex: 1.6 },
      { label: "Entrega", flex: 1, align: "center" },
      { label: "Vencida", flex: 1, align: "center" },
      { label: "Estado", flex: 1.8 },
      { label: "Monto", flex: 1.2, align: "right" },
    ],
    mapRow: (r) => [
      `#${r.id}`,
      r.cliente || "—",
      r.proveedor || "—",
      fmtFecha(r.fecha_entrega),
      `${r.dias} día${Number(r.dias) !== 1 ? "s" : ""}`,
      r.estado_actual || "—",
      fmtMonto(r.monto_total, r.moneda),
    ],
  },
  {
    key: "cotizaciones_viejas",
    tipo: "cotizaciones_viejas",
    titulo: "Cotizaciones pendientes +14 días",
    descripcion: "Cotizaciones sin resolver (ni aceptadas ni rechazadas) con más de 14 días de antigüedad.",
    columnas: [
      { label: "N°", flex: 0.7 },
      { label: "Cliente", flex: 2.2 },
      { label: "Vendedor", flex: 1.6 },
      { label: "Creada", flex: 1, align: "center" },
      { label: "Días", flex: 0.8, align: "center" },
      { label: "Monto", flex: 1.2, align: "right" },
    ],
    mapRow: (r) => [
      `#${r.id}`,
      r.cliente || "—",
      r.usuario || "—",
      fmtFecha(r.created_at),
      `${r.dias}`,
      fmtMonto(r.monto_total, r.moneda),
    ],
  },
];

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { background: "#fff", borderRadius: 16, padding: 26, width: "100%", maxWidth: 560, boxShadow: "0 10px 40px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: "#222" },
  sub: { margin: "4px 0 20px", fontSize: 13, color: "#888" },
  x: { background: "none", border: "none", fontSize: 22, color: "#999", cursor: "pointer", lineHeight: 1 },
  card: { border: "1.5px solid #f0e6ec", borderRadius: 12, padding: "16px 18px", marginBottom: 14 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#9f0059", margin: 0 },
  cardDesc: { fontSize: 12.5, color: "#777", margin: "5px 0 12px", lineHeight: 1.4 },
  cardFoot: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  count: (n) => ({ fontSize: 13, fontWeight: 700, color: n > 0 ? "#dc2626" : "#16a34a" }),
  btn: { display: "inline-block", padding: "8px 16px", background: "#9f0059", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none" },
  btnLoad: { padding: "8px 16px", background: "#cbb3c2", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 },
  selectCli: { padding: "8px 10px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, color: "#333", background: "#fff", outline: "none", cursor: "pointer" },
};

const ReporteCard = ({ rep }) => {
  const [filas, setFilas]   = useState(null); // null = cargando
  const [error, setError]   = useState(false);

  useEffect(() => {
    axios.post(`${BASE}/reportes_datos.php`, new URLSearchParams({ tipo: rep.tipo }))
      .then((r) => { if (r.data?.success) setFilas(r.data.filas || []); else setError(true); })
      .catch(() => setError(true));
  }, [rep.tipo]);

  const cargando = filas === null && !error;
  const n = filas?.length ?? 0;
  const filasPDF = (filas || []).map(rep.mapRow);
  const subtitulo = `Generado el ${fmtFecha(new Date())} · ${n} resultado${n !== 1 ? "s" : ""}`;

  return (
    <div style={S.card}>
      <p style={S.cardTitle}>{rep.titulo}</p>
      <p style={S.cardDesc}>{rep.descripcion}</p>
      <div style={S.cardFoot}>
        {error ? (
          <span style={{ fontSize: 13, color: "#dc2626" }}>Error al cargar</span>
        ) : cargando ? (
          <span style={{ fontSize: 13, color: "#999" }}>Calculando…</span>
        ) : (
          <span style={S.count(n)}>{n === 0 ? "✓ Sin novedades" : `${n} resultado${n !== 1 ? "s" : ""}`}</span>
        )}

        {!cargando && !error && (
          <PDFDownloadLink
            document={
              <ReportePDF
                titulo={rep.titulo}
                subtitulo={subtitulo}
                columnas={rep.columnas}
                filas={filasPDF}
                logoUrl={LOGO}
              />
            }
            fileName={`reporte_${rep.key}_${hoyISO()}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <span style={loading ? S.btnLoad : S.btn}>⬇ {loading ? "Preparando…" : "Descargar PDF"}</span>
            )}
          </PDFDownloadLink>
        )}
      </div>
    </div>
  );
};

// ─── Card parametrizada: detalle por cliente ─────────────────────────────────
const ReporteClienteCard = () => {
  const [clientes,  setClientes]  = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [meses,     setMeses]     = useState(6);
  const [data,      setData]      = useState(null); // { cliente, ordenes }
  const [cargando,  setCargando]  = useState(false);

  useEffect(() => {
    axios.post(`${BASE}/reportes_clientes.php`)
      .then((r) => setClientes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!clienteId) { setData(null); return; }
    setCargando(true);
    axios.post(`${BASE}/reportes_datos.php`, new URLSearchParams({ tipo: "detalle_cliente", cliente_id: clienteId, meses }))
      .then((r) => setData(r.data?.success ? r.data : null))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [clienteId, meses]);

  const ordenes = data?.ordenes ?? [];
  const totalProductos = ordenes.reduce((s, o) => s + (o.items?.length || 0), 0);
  const nombreCliente = clientes.find((c) => String(c.id) === String(clienteId))?.nombre || data?.cliente || "";

  return (
    <div style={S.card}>
      <p style={S.cardTitle}>Detalle por cliente</p>
      <p style={S.cardDesc}>Elegí un cliente y descargá todas sus órdenes de compra del período, con los productos de cada una.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <select style={{ ...S.selectCli, flex: 1, minWidth: 160 }} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
          <option value="">— Elegí un cliente —</option>
          {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.ocs})</option>)}
        </select>
        <select style={S.selectCli} value={meses} onChange={(e) => setMeses(Number(e.target.value))}>
          <option value={3}>3 meses</option>
          <option value={6}>6 meses</option>
          <option value={12}>12 meses</option>
        </select>
      </div>

      <div style={S.cardFoot}>
        {!clienteId ? (
          <span style={{ fontSize: 13, color: "#999" }}>Elegí un cliente</span>
        ) : cargando ? (
          <span style={{ fontSize: 13, color: "#999" }}>Calculando…</span>
        ) : (
          <span style={S.count(ordenes.length)}>
            {ordenes.length === 0 ? "Sin órdenes en el período" : `${ordenes.length} orden${ordenes.length !== 1 ? "es" : ""} · ${totalProductos} producto${totalProductos !== 1 ? "s" : ""}`}
          </span>
        )}

        {clienteId && !cargando && (
          <PDFDownloadLink
            document={<ReporteClientePDF cliente={nombreCliente} meses={meses} ordenes={ordenes} logoUrl={LOGO} />}
            fileName={`detalle_${(nombreCliente || "cliente").replace(/[^a-z0-9]+/gi, "_")}_${hoyISO()}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => <span style={loading ? S.btnLoad : S.btn}>⬇ {loading ? "Preparando…" : "Descargar PDF"}</span>}
          </PDFDownloadLink>
        )}
      </div>
    </div>
  );
};

const ReportesModal = ({ onClose }) => (
  <div style={S.overlay} onClick={onClose}>
    <div style={S.modal} onClick={(e) => e.stopPropagation()}>
      <div style={S.head}>
        <h2 style={S.title}>📄 Reportes</h2>
        <button style={S.x} onClick={onClose}>×</button>
      </div>
      <p style={S.sub}>Descargá el PDF para mandarlo como recordatorio.</p>
      {REPORTES.map((rep) => <ReporteCard key={rep.key} rep={rep} />)}
      <ReporteClienteCard />
    </div>
  </div>
);

export default ReportesModal;
